import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type ProjectEmailMappingType = 'category' | 'marker';

export interface ProjectEmailMapping {
  id: string;
  tenant_id: string;
  project_id: string;
  match_type: ProjectEmailMappingType;
  value: string;
  created_by: string | null;
  created_at: string;
}

interface AddMappingParams {
  matchType: ProjectEmailMappingType;
  value: string;
}

export const useProjectEmailMappings = (projectId: string) => {
  const { user, tenantId } = useAuth();
  const [mappings, setMappings] = useState<ProjectEmailMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) {
      setMappings([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await supabase
      .from('project_email_mappings')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Failed to load project email mappings:', error);
    } else {
      setMappings((data as ProjectEmailMapping[]) || []);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const addMapping = useCallback(
    async ({ matchType, value }: AddMappingParams): Promise<{ ok: true } | { ok: false; error: string }> => {
      if (!user) return { ok: false, error: 'Not authenticated' };
      if (!projectId) return { ok: false, error: 'No project selected' };

      setSubmitting(true);

      const { error } = await supabase.from('project_email_mappings').insert({
        project_id: projectId,
        match_type: matchType,
        value,
        tenant_id: tenantId ?? undefined,
      });

      setSubmitting(false);

      if (error) {
        if (error.code === '23505') {
          return { ok: false, error: 'This category is already mapped to another project.' };
        }
        return { ok: false, error: error.message };
      }

      await load();
      return { ok: true };
    },
    [user, tenantId, projectId, load],
  );

  const removeMapping = useCallback(
    async (id: string): Promise<{ ok: true } | { ok: false; error: string }> => {
      setSubmitting(true);

      const { error, data } = await supabase.from('project_email_mappings').delete().eq('id', id).select();

      setSubmitting(false);
      if (error) return { ok: false, error: error.message };

      if (!data || data.length === 0) {
        return { ok: false, error: 'You do not have permission to remove this mapping.' };
      }

      await load();
      return { ok: true };
    },
    [load],
  );

  return { mappings, loading, submitting, addMapping, removeMapping, reload: load };
};
